import React, { useEffect, useState } from 'react';
import { Form, Col, Row, message, Typography, Space, UploadProps, Upload, Popconfirm } from 'antd';
import { Input } from '@app/components/common/inputs/Input/Input';
import { Button } from '@app/components/common/buttons/Button/Button';
import { UploadOutlined } from '@ant-design/icons';
import { Card } from '@app/components/common/Card/Card';
import { FirmwareModel } from './types';
import { useLocation, useNavigate } from 'react-router-dom';
import { postFirmware } from '@app/api/admin.api';
import { notificationController } from '@app/controllers/notificationController';
import { Checkbox } from '@app/components/common/Checkbox/Checkbox';


interface LocationState {
    record: FirmwareModel;
}

const CreateFirmwarePage: React.FC = () => {
    const [isUpdating, setIsUpdating] = useState(false)
    const [imagePreviewUrl, setImagePreviewUrl] = useState(null);

    const { state } = useLocation();
    const record = state && (state as LocationState).record;
    const navigate = useNavigate();

    useEffect(() => {
        if (record) {
            console.log("IS UPDATING")
            setIsUpdating(true)
            setImagePreviewUrl(record.file)
        }
    }, [record])


    const onFinish = (values: any) => {
        const combinedValues = { ...record, ...values }
        console.log('combinedValues: ', combinedValues);
        return new Promise(() => {
            const bodyFormData = new FormData();
            bodyFormData.append('version', combinedValues.version);
            if (combinedValues.file) {
                bodyFormData.append('file', combinedValues.file.file);
            } else {
                console.error('No valid hex file found.');
            }
            // Optional .bin used by the WiFi OTA flow; the .hex stays for BLE OTA.
            if (combinedValues.bin_file?.file) {
                bodyFormData.append('bin_file', combinedValues.bin_file.file);
            }
            postFirmware(bodyFormData)
                .then((res) => {
                    console.log('res', res);
                    notificationController.success({ message: 'Content uploaded successfully.', duration: 5 });
                    navigate(-1);
                })
                .catch((error) => {
                    console.log('error: ', error);
                    notificationController.error({ message: error, duration: 5 });
                });
        });
    };

    const uploadPropsFor = (extension: string): UploadProps => ({
        beforeUpload: (file: any) => {
            if (!file.name.toLowerCase().endsWith(extension)) {
                message.error(`${file.name} is not a valid ${extension} file`);
                return Upload.LIST_IGNORE;
            }

            return false; // prevent auto-upload, but allow file to be shown in UI
        },
    });

    const props = uploadPropsFor('.hex');
    const binProps = uploadPropsFor('.bin');

    return (
        <Space size={'large'} direction={'vertical'}>
            <Typography.Title level={3}>Upload Firmware</Typography.Title>
            <Card>
                <Form
                    name={'notification-form'}
                    layout={'vertical'}
                    initialValues={record}
                    onFinish={onFinish}
                    autoComplete={'off'}>
                    <Row gutter={{ xs: 10, md: 16, xl: 24 }}>
                        <Col xs={24}>
                            <Form.Item
                                name="version"
                                label="Version"
                                rules={[
                                    { required: true, message: 'Please input a firmware version' },
                                    { pattern: /^\d+\.\d+$/, message: 'Please enter a valid version like 1.0 or 2.34' },
                                ]}
                            >
                                <Input />
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                name={'file'}
                                label={'Firmware File (.hex — BLE OTA)'}
                                rules={[{ required: true, message: 'Please upload a firmware file' }]}>
                                <Upload maxCount={1} {...props}>
                                    <Button icon={<UploadOutlined />}>{isUpdating ? 'Change firmware file' : 'Upload Firmware file'}</Button>
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Col xs={24}>
                            <Form.Item
                                name={'bin_file'}
                                label={'Firmware File (.bin — WiFi OTA)'}>
                                <Upload maxCount={1} {...binProps}>
                                    <Button icon={<UploadOutlined />}>{isUpdating ? 'Change .bin file' : 'Upload .bin file'}</Button>
                                </Upload>
                            </Form.Item>
                        </Col>
                        <Space />
                        <Col xs={24} md={12}>
                            <Form.Item wrapperCol={{ offset: 6, span: 18 }}>
                                <Button type={'ghost'} block htmlType={'submit'}>
                                    {'Create'}
                                </Button>
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item wrapperCol={{ span: 18 }}>
                                <Button danger type={'ghost'} block onClick={() => navigate(-1)}>
                                    Cancel
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </Space >
    )
}

export default CreateFirmwarePage;